import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

export function Dashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    totalClients: 0,
    scheduledJobs: 0,
    completedThisMonth: 0,
  });
  const [upcomingJobs, setUpcomingJobs] = useState<any[]>([]);

  useEffect(() => {
    async function load() {
      try {
        const [clients, jobs] = await Promise.all([
          api("/clients"),
          api("/jobs"),
        ]);

        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        setStats({
          totalClients: clients.length,
          scheduledJobs: jobs.filter((j: any) => j.status === "scheduled").length,
          completedThisMonth: jobs.filter(
            (j: any) =>
              j.status === "completed" && new Date(j.completed_at) >= monthStart
          ).length,
        });

        setUpcomingJobs(
          jobs
            .filter(
              (j: any) =>
                j.status === "scheduled" && new Date(j.scheduled_at) >= now
            )
            .sort(
              (a: any, b: any) =>
                new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime()
            )
            .slice(0, 5)
        );
      } catch {
        // ignore
      }
    }
    load();
  }, []);

  const statCards = [
    {
      label: t("dashboard.totalClients"),
      value: stats.totalClients,
      icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
      color: "text-sky-500 bg-sky-50",
    },
    {
      label: t("dashboard.scheduledJobs"),
      value: stats.scheduledJobs,
      icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
      color: "text-amber-500 bg-amber-50",
    },
    {
      label: t("dashboard.completedThisMonth"),
      value: stats.completedThisMonth,
      icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
      color: "text-emerald-500 bg-emerald-50",
    },
  ];

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          {t("dashboard.title")}
        </h1>
        <p className="text-sm text-navy-400 mt-1">
          {t("dashboard.subtitle")}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
        {statCards.map((card) => (
          <div
            key={card.label}
            className="bg-white rounded-xl border border-navy-100 p-5 card-lift"
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-navy-500 font-medium">{card.label}</p>
              <div className={`w-9 h-9 rounded-lg ${card.color} flex items-center justify-center`}>
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={card.icon} />
                </svg>
              </div>
            </div>
            <p className="text-3xl font-bold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              {card.value}
            </p>
          </div>
        ))}
      </div>

      {/* Upcoming Jobs */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold" style={{ fontFamily: "var(--font-display)" }}>
          {t("dashboard.upcomingJobs")}
        </h2>
        <Link to="/app/schedule" className="text-sm text-amber-600 font-medium hover:text-amber-700 transition-colors">
          {t("dashboard.viewAll")}
        </Link>
      </div>

      {upcomingJobs.length === 0 ? (
        <div className="bg-white rounded-xl border border-navy-100 p-12 text-center">
          <div className="w-12 h-12 bg-navy-50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-navy-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-navy-500 text-sm">{t("jobs.noJobs")}</p>
          <Link to="/app/schedule" className="btn btn-primary btn-sm mt-4">
            {t("jobs.addJob")}
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
          {upcomingJobs.map((job, i) => (
            <div
              key={job.id}
              className={`px-5 py-4 flex items-center justify-between hover:bg-navy-50/50 transition-colors ${
                i < upcomingJobs.length - 1 ? "border-b border-navy-50" : ""
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-sky-50 rounded-lg flex items-center justify-center text-sky-500 shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-navy-900">{job.location_name}</p>
                  <p className="text-xs text-navy-400">{job.client_name}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-navy-700">
                  {new Date(job.scheduled_at).toLocaleDateString()}
                </p>
                <p className="text-xs text-navy-400">
                  {new Date(job.scheduled_at).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
